import React, { useState, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { IconLoader2 } from '@tabler/icons-react';
import { SearchParam } from '@common/ts';
import useTheme from 'chat-ui/src/hooks/useTheme';
import { v4 as uuidv4 } from 'uuid';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import useResultTabStore from '@/store/ResultsTabStore';
import ConversationMessage from '@/components/chat/ConversationMessage';
import Markdown from '@/components/markdown/Markdown';

import { useGetConversation } from '@/reactQuery/get';
import { NavigationTabs } from '@/enums';

function TextWithClickableQuotes({ text }: { text: string }) {
  const { currentTheme } = useTheme();
  const { project_id: projectId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { setActiveTab } = useResultTabStore();

  const [isHovered, setIsHovered] = useState(false);
  const [hoveredConversationId, setHoveredConversationId] = useState('');
  const hoverTimeout = useRef<NodeJS.Timeout | null>(null);

  const { data: conversation, isLoading } = useGetConversation(projectId, hoveredConversationId);

  const formattedConversation = conversation?.messages;

  const addQueryParams = (params: Record<string, string>) => {
    Object.entries(params).forEach(([key, value]) => {
      searchParams.set(key, value);
    });
    setSearchParams(searchParams);
  };

  const openClickedQuote = (id: string) => {
    setActiveTab(NavigationTabs.TRANSCRIPTS);
    addQueryParams({ [SearchParam.SELECTED_TRANSCRIPT]: id });
    addQueryParams({ [SearchParam.CONVERSATION_FROM_QUESTIONS_TAB]: 'true' });
  };

  const handleMouseEnter = (conversationId: string) => {
    if (hoverTimeout.current) {
      clearTimeout(hoverTimeout.current);
    }
    setHoveredConversationId(conversationId);
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    hoverTimeout.current = setTimeout(() => {
      setHoveredConversationId('');
      setIsHovered(false);
    }, 200);
  };

  const parseText = (input: string) => {
    const regex = /<quote>\s*{([^}]*)}\s*<\/quote>/g;
    const result: React.ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    // eslint-disable-next-line no-cond-assign
    while ((match = regex.exec(input)) !== null) {
      const before = input.slice(lastIndex, match.index);
      if (before) {
        result.push(
          <Markdown key={uuidv4()} isParagraphSpanEl>
            {before}
          </Markdown>,
        );
      }

      const quoteContent = match[1];
      try {
        const sanitizedContent = `{${
          quoteContent
            .replace(/'/g, '"') // Replace single quotes with double quotes
            .replace(/([a-zA-Z_]+):/g, '"$1":') // Quote object keys
            .replace(/:\s*([a-zA-Z0-9-]+)/g, ': "$1"') // Wrap values with quotes
            .replace(/: undefined/g, ': null') // Replace `undefined` with `null`
        }}`;
        const quoteData = JSON.parse(sanitizedContent);

        result.push(
          <TooltipProvider delayDuration={0} key={uuidv4()}>
            <span
              onMouseEnter={() => handleMouseEnter(quoteData.conversationId)}
              onMouseLeave={handleMouseLeave}
              className="relative inline-block"
            >
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    style={{ backgroundColor: currentTheme?.primary }}
                    type="button"
                    onClick={() => openClickedQuote(quoteData.conversationId)}
                    className="inline-flex items-center justify-center min-w-6 h-6 px-1.5 mx-1 font-semibold text-xs text-white rounded-full opacity-90"
                  >
                    {quoteData.respondent_index != null ? Number(quoteData.respondent_index) : '?'}
                  </button>
                </TooltipTrigger>
                {isHovered && (
                  <TooltipContent className="w-[350px] bg-white border-2 shadow h-[250px] mx-3 z-100">
                    {isLoading ? (
                      <div className="flex items-center justify-center w-full h-full">
                        <IconLoader2 size={32} className="m-auto animate-spin text-dark-text" />
                      </div>
                    ) : (
                      <div className="relative flex flex-col w-full h-full px-2 overflow-hidden overflow-y-auto">
                        <div className="flex flex-col w-full gap-1 pb-2 mb-2">
                          {formattedConversation?.map((item) => (
                            <ConversationMessage key={item?._id} {...item} />
                          ))}
                        </div>
                      </div>
                    )}
                  </TooltipContent>
                )}
              </Tooltip>
            </span>
          </TooltipProvider>,
        );
      } catch (error) {
        console.error('Failed to parse quote content:', quoteContent, error);
      }

      lastIndex = regex.lastIndex;
    }

    const after = input.slice(lastIndex);
    if (after)
      result.push(
        <Markdown isParagraphSpanEl key={uuidv4()}>
          {after}
        </Markdown>,
      );

    return result;
  };

  return parseText(text);
}

export default TextWithClickableQuotes;
